import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { post, patch } from '../../../helpers/apiHelper';

const UserForm = ({ user, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        email: '',
        nombre: '',
        apellido: '',
        segundoapellido: '',
        pais: 'España',
        role: 'USER_ROLE',
        roles: 'user',
        plan: 'trial',
        pagoPlan: 'monthly',
        password: '', // Solo requerido para nuevos usuarios
        isEmailVerified: false,
        hasPaid: false,
        trialStarted: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (user) {
            // Excluir campos sensibles o que no queremos editar
            const {
                password,
                __v,
                register_at,
                validated, // Eliminar validated del destructuring
                ...editableFields
            } = user;

            setFormData({
                ...editableFields,
                password: '' // No mostramos la contraseña actual
            });
        }
    }, [user]);

    const validateForm = () => {
        const newErrors = {};
        const paisesValidos = ['España', 'Chile', 'Perú'];

        if (!formData.email) {
            newErrors.email = 'El email es requerido';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email inválido';
        }

        if (!formData.nombre) {
            newErrors.nombre = 'El nombre es requerido';
        }

        if (!formData.apellido) {
            newErrors.apellido = 'El apellido es requerido';
        }

        if (!paisesValidos.includes(formData.pais)) {
            newErrors.pais = `El país debe ser uno de: ${paisesValidos.join(', ')}`;
        }

        if (!user && !formData.password) {
            newErrors.password = 'La contraseña es requerida para nuevos usuarios';
        } else if (!user && formData.password.length < 8) {
            newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            let response;

            // Preparar datos para enviar
            const dataToSend = { ...formData };

            // Debug: Mostrar el valor del país que se está enviando
            console.log('País a enviar:', {
                valor: dataToSend.pais,
                tipo: typeof dataToSend.pais,
                longitud: dataToSend.pais.length,
                codigoAscii: [...dataToSend.pais].map(char => char.charCodeAt(0))
            });

            // Limpiar datos antes de enviar
            Object.keys(dataToSend).forEach(key => {
                if (dataToSend[key] === '') {
                    delete dataToSend[key];
                }
            });

            // Si estamos editando y no se cambió la contraseña, la eliminamos del payload
            if (user && !dataToSend.password) {
                delete dataToSend.password;
            }

            // Eliminar campos que no deberían actualizarse
            delete dataToSend._id;
            delete dataToSend.__v;
            delete dataToSend.register_at;
            delete dataToSend.compras;
            delete dataToSend.totales;
            delete dataToSend.favoritos;
            delete dataToSend.tickets;
            delete dataToSend.trabajador;
            delete dataToSend.validated; // Asegurarnos de que este campo no se envíe

            // Guardar los valores originales para comparar
            const originalValues = {
                role: dataToSend.role,
                plan: dataToSend.plan,
                // Agregar otros campos importantes que quieras monitorear
            };

            if (user && user._id) {
                // Actualizar usuario existente usando PATCH
                console.log('Updating user with ID:', user._id);
                console.log('Original values:', originalValues);
                console.log('Data to send:', dataToSend);

                response = await patch(`users/${user._id}`, dataToSend);

                // Verificar si los cambios se aplicaron
                console.log('Response values:', {
                    role: response.role,
                    plan: response.plan,
                    // validated: response.validated // Eliminar validated de la verificación
                });

                // Verificar cambios no aplicados
                const unchangedFields = Object.keys(originalValues).filter(key =>
                    originalValues[key] !== response[key]
                );

                if (unchangedFields.length > 0) {
                    console.warn('Warning: Some fields were not updated:', unchangedFields);
                    console.warn('Original values:', originalValues);
                    console.warn('Response values:', {
                        role: response.role,
                        plan: response.plan,
                        // validated: response.validated // Eliminar validated de la verificación
                    });
                }
            } else {
                // Crear nuevo usuario
                console.log('Creating new user');
                console.log('Data to send:', dataToSend);
                response = await post('users', dataToSend);
            }

            console.log('Server response:', response);

            // Verificar si la respuesta es exitosa pero los datos no se actualizaron
            if (response && response.role !== dataToSend.role) {
                setErrors({
                    submit: 'Los cambios fueron recibidos pero no se aplicaron completamente. Posible problema de permisos.'
                });
                return;
            }

            onSuccess(response);
            onClose();
        } catch (error) {
            console.error('Error saving user:', error);
            setErrors({
                submit: error.message || 'Error al guardar el usuario'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Limpiar error del campo cuando el usuario empieza a escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleToggle = (field) => {
        setFormData(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    // Manejar cierre con Escape
    const handleEscape = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [handleEscape]);

    // Prevenir scroll del body cuando el modal está abierto
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
            <div className="flex items-end sm:items-center justify-center min-h-screen p-0 sm:p-4 text-center sm:block">
                {/* Overlay de fondo */}
                <div
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                ></div>

                {/* Centrado del modal */}
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                {/* Modal */}
                <div className="inline-block w-full align-bottom sm:align-middle bg-white rounded-t-lg sm:rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-2xl sm:w-full">
                    {/* Header */}
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="flex justify-between items-center pb-3 border-b">
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-900" id="modal-title">
                                {user ? 'Editar Usuario' : 'Nuevo Usuario'}
                            </h3>
                            <button
                                onClick={onClose}
                                className="text-gray-400 hover:text-gray-500 transition-colors p-2 sm:p-0"
                                aria-label="Cerrar"
                            >
                                <FontAwesomeIcon icon={faTimes} className="text-xl" />
                            </button>
                        </div>

                        {/* Formulario */}
                        <div className="mt-4">
                            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                                {/* Sección de datos personales */}
                                <div className="space-y-4 sm:space-y-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'
                                                }`}
                                            placeholder="usuario@ejemplo.com"
                                        />
                                        {errors.email && (
                                            <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Nombre
                                            </label>
                                            <input
                                                type="text"
                                                name="nombre"
                                                value={formData.nombre}
                                                onChange={handleChange}
                                                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${errors.nombre ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                            {errors.nombre && (
                                                <p className="mt-1 text-sm text-red-600">{errors.nombre}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Primer Apellido
                                            </label>
                                            <input
                                                type="text"
                                                name="apellido"
                                                value={formData.apellido}
                                                onChange={handleChange}
                                                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${errors.apellido ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                            />
                                            {errors.apellido && (
                                                <p className="mt-1 text-sm text-red-600">{errors.apellido}</p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Segundo Apellido
                                            </label>
                                            <input
                                                type="text"
                                                name="segundoapellido"
                                                value={formData.segundoapellido || ''}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                País
                                            </label>
                                            <select
                                                name="pais"
                                                value={formData.pais}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="España">España</option>
                                                <option value="Chile">Chile</option>
                                                <option value="Perú">Perú</option>
                                            </select>
                                            {errors.pais && (
                                                <p className="mt-1 text-sm text-red-600">{errors.pais}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Rol
                                            </label>
                                            <select
                                                name="role"
                                                value={formData.role}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="USER_ROLE">Usuario</option>
                                                <option value="ADMIN_ROLE">Administrador</option>
                                            </select>
                                        </div>
                                    </div>

                                    {!user && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Contraseña
                                            </label>
                                            <input
                                                type="password"
                                                name="password"
                                                value={formData.password}
                                                onChange={handleChange}
                                                className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="Mínimo 8 caracteres"
                                            />
                                            {errors.password && (
                                                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Sección de plan */}
                                <div className="space-y-4 border-t pt-4">
                                    <h4 className="text-base sm:text-lg font-medium text-gray-900">Plan y Pagos</h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Plan
                                            </label>
                                            <select
                                                name="plan"
                                                value={formData.plan}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="trial">Trial</option>
                                                <option value="basic">Básico</option>
                                                <option value="professional">Profesional</option>
                                                <option value="enterprise">Empresa</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Frecuencia de Pago
                                            </label>
                                            <select
                                                name="pagoPlan"
                                                value={formData.pagoPlan}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                            >
                                                <option value="monthly">Mensual</option>
                                                <option value="annual">Anual</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Estados */}
                                <div className="space-y-4 border-t pt-4">
                                    <h4 className="text-base sm:text-lg font-medium text-gray-900">Estados</h4>
                                    <div className="space-y-4">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg gap-4 sm:gap-2">
                                            <div>
                                                <h5 className="text-sm font-medium text-gray-900">Verificación de Email</h5>
                                                <p className="text-xs text-gray-500">
                                                    Indica si el usuario ha verificado su dirección de email
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleToggle('isEmailVerified')}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.isEmailVerified ? 'bg-green-500' : 'bg-gray-200'
                                                    }`}
                                            >
                                                <span
                                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.isEmailVerified ? 'translate-x-5' : 'translate-x-0'
                                                        }`}
                                                />
                                            </button>
                                        </div>

                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gray-50 rounded-lg gap-4 sm:gap-2">
                                            <div>
                                                <h5 className="text-sm font-medium text-gray-900">Estado de Pago</h5>
                                                <p className="text-xs text-gray-500">
                                                    Indica si el usuario ha realizado el pago correspondiente
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleToggle('hasPaid')}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 ${formData.hasPaid ? 'bg-green-500' : 'bg-gray-200'
                                                    }`}
                                            >
                                                <span
                                                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.hasPaid ? 'translate-x-5' : 'translate-x-0'
                                                        }`}
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Error general */}
                                {errors.submit && (
                                    <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                                        {errors.submit}
                                    </div>
                                )}

                                {/* Botones de acción */}
                                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white p-4 sm:p-0">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="w-full sm:w-auto px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`w-full sm:w-auto px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center justify-center gap-2 ${loading ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}
                                    >
                                        <FontAwesomeIcon icon={faSave} />
                                        {loading ? 'Guardando...' : 'Guardar'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserForm; 